// Shared tool configuration used by AllTools, Guide, Sidebar, and SearchBar.
// When adding a new tool, add it here and it will appear everywhere automatically.

import {
  Terminal, FileJson, Braces, Hash, Key, Fingerprint, FileSpreadsheet, Timer,
  AtSign, History, Calendar, FileCode, Type, AlignLeft, FileText, Ruler,
  FileSearch, Files, Scissors, Image, FileUser, Lock, Gauge, Droplets,
  Clock, Twitter, MessageSquare, Webhook,
  QrCode, ImageIcon, Maximize, Crop, Palette, Camera, Smile, Eraser, RotateCw,
  Wand2, Sparkles, RefreshCw, Search, Bot,
  Crown, ScanLine, BarChart3, Shuffle, Hourglass, Wrench, Mail, Code, Share2, Zap
} from 'lucide-react';

export type ToolCategory = 'ai' | 'developer' | 'email' | 'document' | 'pdf' | 'social' | 'design' | 'utilities' | 'pro';

export interface ToolConfig {
  name: string;
  description: string;
  path: string;
  icon: React.ElementType;
  category: ToolCategory;
  isNew?: boolean;
  isPro?: boolean;
}

export const tools: ToolConfig[] = [
  // AI Tools
  { name: 'AI Humanizer', description: 'Humanize AI-generated text naturally', path: '/dashboard/tools/ai-humanizer', icon: Wand2, category: 'ai', isNew: true },
  { name: 'Prompt Generator', description: 'Create optimized AI prompts', path: '/dashboard/tools/prompt-generator', icon: Sparkles, category: 'ai', isNew: true },
  { name: 'Paraphraser', description: 'Rewrite text in different styles', path: '/dashboard/tools/paraphraser', icon: RefreshCw, category: 'ai', isNew: true },
  { name: 'Plagiarism Checker', description: 'Check text for plagiarism', path: '/dashboard/tools/plagiarism-checker', icon: Search, category: 'ai', isNew: true },
  { name: 'AI Detector', description: 'Detect AI-generated content', path: '/dashboard/tools/ai-detector', icon: Bot, category: 'ai', isNew: true },

  // Developer Tools
  { name: 'Code Studio', description: 'Live HTML/CSS/JS editor with preview — New & Improved!', path: '/dashboard/tools/code-studio', icon: Terminal, category: 'developer' },
  { name: 'JSON Formatter', description: 'Format and validate JSON data', path: '/dashboard/tools/json-formatter', icon: FileJson, category: 'developer' },
  { name: 'Base64 Tool', description: 'Encode and decode Base64 strings', path: '/dashboard/tools/base64', icon: Braces, category: 'developer' },
  { name: 'Hash Generator', description: 'Generate MD5, SHA-1, SHA-256 hashes', path: '/dashboard/tools/hash-generator', icon: Hash, category: 'developer' },
  { name: 'Password Generator', description: 'Create secure random passwords', path: '/dashboard/tools/password-generator', icon: Key, category: 'developer' },
  { name: 'UUID Generator', description: 'Generate unique identifiers', path: '/dashboard/tools/uuid-generator', icon: Fingerprint, category: 'developer' },
  { name: 'CSV ↔ JSON', description: 'Convert between CSV and JSON formats', path: '/dashboard/tools/csv-json', icon: FileSpreadsheet, category: 'developer', isNew: true },
  { name: 'Timestamp Converter', description: 'Convert Unix timestamps to dates', path: '/dashboard/tools/timestamp-converter', icon: Timer, category: 'developer', isNew: true },

  // Email Tools
  { name: 'Temp Mail', description: 'Generate temporary email addresses', path: '/dashboard/tools/temp-mail', icon: AtSign, category: 'email', isNew: true },
  { name: 'Mail History', description: 'View past temp mail sessions', path: '/dashboard/tools/temp-mail/history', icon: History, category: 'email' },

  // Document Tools
  { name: 'Days Calculator', description: 'Calculate days between dates', path: '/dashboard/tools/days-calculator', icon: Calendar, category: 'document', isNew: true },
  { name: 'Word Counter', description: 'Count words, characters, sentences', path: '/dashboard/tools/word-counter', icon: FileCode, category: 'document', isNew: true },
  { name: 'Lorem Ipsum', description: 'Generate placeholder text', path: '/dashboard/tools/lorem-ipsum', icon: Type, category: 'document', isNew: true },
  { name: 'Text Case Converter', description: 'Convert text between cases', path: '/dashboard/tools/text-case', icon: AlignLeft, category: 'document', isNew: true },
  { name: 'Markdown to HTML', description: 'Convert Markdown to HTML', path: '/dashboard/tools/markdown-html', icon: FileText, category: 'document', isNew: true },
  { name: 'Unit Converter', description: 'Convert length, weight, temperature, time', path: '/dashboard/tools/unit-converter', icon: Ruler, category: 'document', isNew: true },

  // PDF Tools
  { name: 'PDF Analyzer', description: 'AI-powered PDF analysis', path: '/dashboard/tools/pdf-analyzer', icon: FileSearch, category: 'pdf', isNew: true },
  { name: 'PDF Editor', description: 'Edit, annotate, and reorder PDF pages', path: '/dashboard/tools/pdf-editor', icon: FileText, category: 'pdf', isNew: true },
  { name: 'PDF Compressor', description: 'Reduce PDF file size', path: '/dashboard/tools/pdf-compressor', icon: Gauge, category: 'pdf', isNew: true },
  { name: 'PDF Password', description: 'Add or remove PDF password protection', path: '/dashboard/tools/pdf-password', icon: Lock, category: 'pdf', isNew: true },
  { name: 'PDF Watermark', description: 'Add text watermarks to PDF pages', path: '/dashboard/tools/pdf-watermark', icon: Droplets, category: 'pdf', isNew: true },
  { name: 'PDF Merge', description: 'Combine multiple PDFs into one', path: '/dashboard/tools/pdf-merge', icon: Files, category: 'pdf', isNew: true },
  { name: 'PDF Splitter', description: 'Split PDF by page ranges', path: '/dashboard/tools/pdf-splitter', icon: Scissors, category: 'pdf', isNew: true },
  { name: 'PDF to Image', description: 'Convert PDF pages to images', path: '/dashboard/tools/pdf-to-image', icon: Image, category: 'pdf', isNew: true },
  { name: 'Resume Builder', description: 'Create professional resumes', path: '/dashboard/tools/resume-builder', icon: FileUser, category: 'pdf', isNew: true },
  { name: 'Office to PDF', description: 'Convert Office documents to PDF', path: '/dashboard/tools/office-to-pdf', icon: FileText, category: 'pdf', isNew: true },

  // Social Tools
  { name: 'Discord Timestamp', description: 'Create Discord timestamps', path: '/dashboard/tools/discord-timestamp', icon: Clock, category: 'social', isNew: true },
  { name: 'Twitter Counter', description: 'Count characters for tweets', path: '/dashboard/tools/twitter-counter', icon: Twitter, category: 'social', isNew: true },
  { name: 'Discord Embed Builder', description: 'Create Discord webhook embeds', path: '/dashboard/tools/discord-embed', icon: MessageSquare, category: 'social', isNew: true },
  { name: 'Webhook Sender', description: 'Send webhook requests', path: '/dashboard/tools/webhook-sender', icon: Webhook, category: 'social', isNew: true },

  // Design Tools
  { name: 'QR Code Generator', description: 'Generate QR codes instantly', path: '/dashboard/tools/qr-code', icon: QrCode, category: 'design', isNew: true },
  { name: 'Image Compressor', description: 'Compress images without quality loss', path: '/dashboard/tools/image-compressor', icon: ImageIcon, category: 'design', isNew: true },
  { name: 'Image Resizer', description: 'Resize images to any dimension', path: '/dashboard/tools/image-resizer', icon: Maximize, category: 'design', isNew: true },
  { name: 'Image Cropper', description: 'Crop images with custom aspect ratios', path: '/dashboard/tools/image-cropper', icon: Crop, category: 'design', isNew: true },
  { name: 'Image Converter', description: 'Convert between PNG, JPG, WebP', path: '/dashboard/tools/image-converter', icon: RefreshCw, category: 'design', isNew: true },
  { name: 'Background Remover', description: 'Remove image backgrounds', path: '/dashboard/tools/background-remover', icon: Eraser, category: 'design', isNew: true },
  { name: 'Image Rotate & Flip', description: 'Rotate and flip images', path: '/dashboard/tools/image-rotate-flip', icon: RotateCw, category: 'design', isNew: true },
  { name: 'Placeholder Image', description: 'Generate placeholder images', path: '/dashboard/tools/placeholder-image', icon: ImageIcon, category: 'design', isNew: true },
  { name: 'Color Picker', description: 'Pick and convert colors', path: '/dashboard/tools/color-picker', icon: Palette, category: 'design', isNew: true },
  { name: 'Favicon Generator', description: 'Create favicons from images', path: '/dashboard/tools/favicon-generator', icon: Image, category: 'design', isNew: true },
  { name: 'Screenshot Beautifier', description: 'Beautify screenshots with backgrounds', path: '/dashboard/tools/screenshot-beautifier', icon: Camera, category: 'design', isNew: true },
  { name: 'Emoji Picker', description: 'Browse and copy emojis', path: '/dashboard/tools/emoji-picker', icon: Smile, category: 'design', isNew: true },

  // Utilities
  { name: 'QR Code Reader', description: 'Decode QR codes from images', path: '/dashboard/tools/qr-reader', icon: ScanLine, category: 'utilities', isNew: true },
  { name: 'Barcode Generator', description: 'Generate barcodes in various formats', path: '/dashboard/tools/barcode-generator', icon: BarChart3, category: 'utilities', isNew: true },
  { name: 'Random Picker', description: 'Randomly pick items from a list', path: '/dashboard/tools/random-picker', icon: Shuffle, category: 'utilities', isNew: true },
  { name: 'Countdown Timer', description: 'Live countdown to any date', path: '/dashboard/tools/countdown-timer', icon: Hourglass, category: 'utilities', isNew: true },
  { name: 'Pomodoro Timer', description: 'Stay focused with timed work sessions', path: '/dashboard/tools/pomodoro-timer', icon: Clock, category: 'utilities', isNew: true },

  // Pro Tools
  { name: 'BIN Generator', description: 'Generate card numbers from BIN', path: '/dashboard/pro-tools', icon: Crown, category: 'pro', isPro: true },
  { name: 'Card Validator', description: 'Validate card numbers using Luhn', path: '/dashboard/pro-tools', icon: Crown, category: 'pro', isPro: true },
  { name: 'BIN Lookup', description: 'Lookup BIN information', path: '/dashboard/pro-tools', icon: Crown, category: 'pro', isPro: true },
];

export const categories = [
  { id: 'all' as const, label: 'All Tools', icon: Wrench },
  { id: 'ai' as ToolCategory, label: 'AI', icon: Sparkles },
  { id: 'developer' as ToolCategory, label: 'Developer', icon: Code },
  { id: 'email' as ToolCategory, label: 'Email', icon: Mail },
  { id: 'document' as ToolCategory, label: 'Document', icon: FileText },
  { id: 'pdf' as ToolCategory, label: 'PDF', icon: Files },
  { id: 'social' as ToolCategory, label: 'Social', icon: Share2 },
  { id: 'design' as ToolCategory, label: 'Design', icon: Palette },
  { id: 'utilities' as ToolCategory, label: 'Utilities', icon: Wrench },
  { id: 'pro' as ToolCategory, label: 'Pro Tools', icon: Crown },
];

export const categoryDescriptions: Record<ToolCategory, string> = {
  ai: 'AI-powered tools for text humanization, paraphrasing, plagiarism detection, and content analysis.',
  developer: 'Essential developer utilities including code editors, formatters, generators, and converters.',
  email: 'Temporary email generation and management for privacy-focused workflows.',
  document: 'Text processing, formatting, and document utility tools.',
  pdf: 'Complete PDF toolkit: edit, merge, split, compress, watermark, convert, and analyze PDFs.',
  social: 'Tools for Discord, Twitter, and webhook integrations.',
  design: 'Image editing, QR codes, color tools, favicons, and visual design utilities.',
  utilities: 'General-purpose tools like timers, barcode generators, and random pickers.',
  pro: 'Premium tools for advanced card validation and BIN operations.',
};

// Tool walkthroughs for the Guide page. 
// To add a walkthrough for a new tool, add an entry here with the tool's path as the key.
export const toolWalkthroughs: Record<string, { title: string; steps: string[]; tips?: string[] }> = {
  '/dashboard/tools/ai-humanizer': {
    title: 'AI Humanizer',
    steps: [
      'Paste your AI-generated text into the input area.',
      'Select a humanization style (e.g., casual, academic, professional).',
      'Click "Humanize" to process the text.',
      'Review the output and copy or download it.',
    ],
    tips: ['Works best with English text.', 'Longer texts produce better results.'],
  },
  '/dashboard/tools/paraphraser': {
    title: 'Paraphraser',
    steps: [
      'Enter or paste the text you want to rewrite.',
      'Choose a rewriting mode (standard, fluent, creative).',
      'Click "Paraphrase" to generate the rewritten version.',
      'Compare original and paraphrased text side by side.',
    ],
  },
  '/dashboard/tools/pdf-merge': {
    title: 'PDF Merge',
    steps: [
      'Upload two or more PDF files using the drop zone.',
      'Drag to reorder the files in your preferred sequence.',
      'Click "Merge PDFs" to combine them.',
      'Download the merged PDF file.',
    ],
    tips: ['Each file must be under 50MB.', 'The tool works entirely in your browser — no files are uploaded to any server.'],
  },
  '/dashboard/tools/code-studio': {
    title: 'Code Studio',
    steps: [
      'Create a new project or open an existing one from the file explorer.',
      'Edit HTML, CSS, and JavaScript files in the Monaco editor.',
      'See changes instantly in the Live Preview panel.',
      'Use TAM AI (right panel) to get code suggestions or ask questions.',
      'Press Ctrl+K (Cmd+K) for inline AI editing on selected code.',
      'Click "Publish" to deploy your site live on tam27.store.',
    ],
    tips: [
      'Use @filename in TAM AI chat to reference specific files.',
      'Ctrl+K works on selected text for targeted edits.',
      'Published sites get their own URL under tam27.store/s/your-slug.',
    ],
  },
  '/dashboard/tools/temp-mail': {
    title: 'Temp Mail',
    steps: [
      'Click "Generate" to create a temporary email address.',
      'Copy the email address and use it on any website.',
      'Incoming emails appear in real-time in the inbox below.',
      'The email expires automatically after the session ends.',
    ],
    tips: ['Each generation costs 0.10 credits.', 'Check Mail History for past sessions.'],
  },
  '/dashboard/tools/resume-builder': {
    title: 'Resume Builder',
    steps: [
      'Fill in your personal information, education, and experience.',
      'Choose a resume template from the available options.',
      'Preview your resume in real-time as you type.',
      'Download the finished resume as a PDF.',
    ],
  },
  '/dashboard/tools/ai-detector': {
    title: 'AI Detector',
    steps: [
      'Paste the text you want to analyze.',
      'Click "Detect" to scan for AI-generated content.',
      'View the confidence score and highlighted sections.',
      'Use the results to improve your content if needed.',
    ],
  },
};
